<script setup lang="ts">
    import { ref, computed, onMounted } from 'vue'
    import { isNullOrWhitespace } from '@common/Common'

    // ---------------- types ----------------

    class MemberReadModel
    {
        WorkspaceUserId!: number
        Email!: string
        Name!: string
    }

    // ---------------- props / emits ----------------

    const props = defineProps<{
        SurveyId: number
        Name: string
        Description: string
        Members: MemberReadModel[]
        OwnerEmail: string
        NameError: string
        ResponseDueDate: string
    }>()

    const emit = defineEmits<{
        (e: 'deletePanel', surveyId: number): void
        (e: 'createPanel', payload: {
            Name: string | null
            Description: string | null
            OwnerEmail: string | undefined
            ResponseDueDate: string | null
        }): void
        (e: 'updatePanel', payload: {
            SurveyId: number
            Name: string | null
            Description: string | null
            OwnerEmail: string | undefined
            ResponseDueDate: string | null
        }): void
        (e: 'close'): void
    }>()

    // ---------------- derived props ----------------

    const surveyId = computed(() => props.SurveyId)
    const nameError = computed(() => props.NameError)

    const title = computed(() =>
        surveyId.value > 0
            ? `Edit panel: ${props.Name}`
            : 'Create opinion panel'
    )

    const createVisible = computed(() => surveyId.value === 0)
    const updateVisible = computed(() => surveyId.value !== 0)
    const deleteVisible = computed(() => surveyId.value !== 0)

    // ---------------- local editable state ----------------

    const name = ref<string | null>(null)
    const description = ref<string | null>(null)
    const members = ref<MemberReadModel[]>([])
    const owner = ref<MemberReadModel | null>(null)
    const responseDueDate = ref<string | null>(null)

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        name.value = props.Name
        description.value = props.Description
        members.value = [...props.Members]
        responseDueDate.value = props.ResponseDueDate

        const ownerEmail = props.OwnerEmail

        if (!isNullOrWhitespace(ownerEmail))
        {
            let found = members.value.find(m => m.Email === ownerEmail)

            if (!found)
            {
                found = new MemberReadModel()
                found.Name = ownerEmail
                found.Email = ownerEmail

                members.value.push(found)
            }

            owner.value = found
        }
    })

    // ---------------- actions ----------------

    function deletePanel()
    {
        emit('deletePanel', surveyId.value)
    }

    function createPanel()
    {
        emit('createPanel', {
            Name: name.value,
            Description: description.value,
            OwnerEmail: owner.value?.Email,
            ResponseDueDate: responseDueDate.value
        })
    }

    function updatePanel()
    {
        emit('updatePanel', {
            SurveyId: surveyId.value,
            Name: name.value,
            Description: description.value,
            OwnerEmail: owner.value?.Email,
            ResponseDueDate: responseDueDate.value
        })
    }

    function close()
    {
        emit('close')
    }
</script>


<template>
    <div class="modal-mask">
        <div class="modal-wrapper">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{title}}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div class="form-group">
                            <label>Name</label>
                            <input v-model.trim="name" type="text" class="form-control">
                            <div class="error">{{nameError}}</div>
                        </div>

                        <div class="form-group">
                            <label>Description</label>
                            <textarea v-model.trim="description" type="text" class="form-control"></textarea>
                        </div>

                        <div class="form-group">
                            <label>Owner</label>
                            <select v-model="owner" class="form-control">
                                <option :value="null">None</option>
                                <option v-for="m in members" :value="m" :key="m.Email">{{m.Name}}</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label>Response due date:</label>
                            <input type="date" class="form-control" v-model="responseDueDate" />
                        </div>
                    </div>

                    <div class="modal-footer justify-content-between">

                        <div class="dropdown dropup">
                            <button v-if="deleteVisible" type="button" class="btn btn-danger" data-toggle="dropdown">Delete</button>
                            <div class="dropdown-menu" role="menu" style="padding: 10px;">
                                <a class="dropdown-item">Cancel</a>
                                <a class="dropdown-item" @click="deletePanel">Confirm</a>
                            </div>
                        </div>

                        <div>
                            <button v-if="createVisible" @click="createPanel" type="button" class="btn btn-primary">Create</button>
                            <button v-if="updateVisible" @click="updatePanel" type="button" class="btn btn-primary">Save</button>
                            <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>